import * as Constants from '../../../Constants'
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { getUserByToken, register } from '../core/_requests'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toAbsoluteUrl } from '../../../../_myb/helpers'
import { PasswordMeterComponent } from '../../../../_myb/assets/ts/components'
import { useAuth } from '../core/Auth'
import { fetchIpData, signup, socialLogin, socialSignup, validateEmail, validateOTP } from '../../../api'
import OtpInput from 'react-otp-input';
import './otp.css';
import { SignupDTO, SocialLogins, SocialSignUpDto } from '../../../types'
import { getCurrentLang } from '../../../../_myb/i18n/LangConfig'
import { CurrencyPicker } from '../../settings/Currency'
import { CountryPicker } from '../../settings/Country'
import { BusinessTypePicker } from '../../settings/BusinessType'
import { authModel, doLogin } from './Login'
import { AuthModel } from '../core/_models'
import { EasyPicker } from '../../widgets/components/SimplePicker'
import { countryData, CountryList, CurrencyList } from '../../../DS-Files'
import { brand, brandSettings } from '../../settings/BrandSettings'
import AppleSignInButton from './AppleLogin' // Import the Apple component
import { trackEvent, AnalyticsEvent } from '../../apps/analytics'

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  changepassword: '',
  acceptTerms: false,
}

const inputStyles = {
  width: '40px',
  height: '40px',
  margin: '0 5px',
  fontSize: '20px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  textAlign: 'center',
};

enum SignupStatus {
  initialized = 'initialized',
  emailVerified = 'emailVerified',
  socialsignVerified = 'socialsignVerified',
  otpVerified = 'otpVerified',
  signupComplete = 'signupComplete'
}

const registrationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  changepassword: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(50, 'Maximum 50 symbols')
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
  acceptTerms: Yup.bool().oneOf([true], 'You must accept the terms and conditions').required('You must accept the terms and conditions'),
})

export const businessTypes = Constants.industryTypes.map((industry, index) => ({
  key: industry.industry_nm,
  label: industry.industry_nm,
  value: industry.industry_nm,
}));

export function Registration() {
  const [loading, setLoading] = useState(false)
  const { saveAuth, setCurrentUser, saveCurrentUser } = useAuth()
  const [signUpInfo, setSignUpInfo] = useState(new SignupDTO())
  const [signUpStatus, setSignUpStatus] = useState(SignupStatus.initialized)
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const validateEmailResponse = await validateEmail(values.email)
        if (validateEmailResponse) {
          setSignUpStatus(SignupStatus.emailVerified)
          setLoading(false)
          setSignUpInfo(prevInfo => ({
            ...prevInfo,
            email: values.email,
            password: values.password
          }));
        }
      } catch (error: any) {
        console.error(error)
        saveAuth(undefined)
        setStatus(error?.message || 'An unexpected error occurred. Please try again.'.toTranslatedDisplay())
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const [otp, setOtp] = useState('');
  const [otpError, setOTPError] = useState('');

  const handleSignupFormInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null) => {
    if (e) {
      const { name, value } = e.target;
      setSignUpInfo(prevInfo => ({
        ...prevInfo,
        [name]: value
      }));
    }
  }

  const country_options = countryData.map((country, index) => ({
    key: country.country_nm,
    text: country.country_nm,
    value: country.country_nm,
  }));

  const handleOTPValidation = async () => {
    try {
      const otpNumber = Number(otp);
      const validateOTPResponse = await validateOTP({ email: signUpInfo.email, otp_num: otpNumber })
      setSignUpStatus(SignupStatus.otpVerified)
      setSignUpInfo(prevInfo => ({
        ...prevInfo,
        otp_num: otpNumber
      }));
    } catch (error) {
      setOTPError('Invalid OTP, Please check!'.toTranslatedDisplay())
      console.error(error)
      saveAuth(undefined)
      setLoading(false)
    }
  }

  const auth: AuthModel = {
    api_token: '',
    refreshToken: undefined
  }

  const handleSignup = async () => {
    try {
      const otpNumber = Number(otp);
      setLoading(true)
      signUpInfo.brand = brand
      signUpInfo.language = getCurrentLang()
      const signupResponse = await signup(signUpInfo)

      setSignUpStatus(SignupStatus.signupComplete)
      const loginResponse = await doLogin({ email: signUpInfo.email, password: signUpInfo.password })
      if (loginResponse) {
        auth.api_token = loginResponse.token
        saveAuth(auth)
        setCurrentUser(loginResponse.user)
        const user = await getUserByToken(authModel.api_token);
        saveCurrentUser(user);
        // MBAI-500/502/517: signup completed (email flow)
        trackEvent(AnalyticsEvent.SignUp, { method: 'email' })
        setLoading(false)
      } else {
        setLoading(false)
        throw new Error('Login Failed')
      }
    } catch (error) {
      setOTPError('Error signingup, Please try again!'.toTranslatedDisplay())
      console.error(error)
      saveAuth(undefined)
      setLoading(false)
    }
  }

  // NEW: Handle Apple Sign-In for Registration
  // Update the handleAppleSignUp function to include loading state:
  const handleAppleSignUp = async (appleData: {
    code: string;
    id_token: string;
    user?: any;
    state?: string;
  }) => {
    setAppleLoading(true);
    try {
      // Send Apple ID token to backend using the same socialLogin function
      const result = await socialLogin({
        sToken: appleData.id_token,
        provider: SocialLogins.Apple,
        onlyLogin: false,
      });

      if (result) {
        // User already exists, log them in
        authModel.api_token = result.token;
        saveAuth(authModel);
        const user = await getUserByToken(authModel.api_token);
        saveCurrentUser(user);
        navigate('/dashboard');
      } else if (appleData.id_token) {
        // User needs to sign up, redirect to social signup
        const signUpInfo = {
          token: appleData.id_token,
          provider: SocialLogins.Apple,
          userData: appleData.user // Include user data if available
        }
        navigate('/auth/social-signup', { state: signUpInfo })
      } else {
        console.error('Apple social signup failed');
        setAppleLoading(false);
      }
    } catch (error) {
      console.error('Error during Apple sign-up:', error);
      setAppleLoading(false);
    }
  };

  useEffect(() => {
    if (signUpStatus === SignupStatus.signupComplete) {
      // Handle signup completion
    }
  }, [signUpStatus])

  useEffect(() => {
    setOTPError('')
    if (otp.length == 6) {
      handleOTPValidation()
    }
  }, [otp])

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Update the handleGoogleSignUp function to include loading state:
  const handleGoogleSignUp = async (response: any) => {
    setGoogleLoading(true);
    try {
      const token = response.credential;

      const result = await socialLogin({
        sToken: token,
        provider: SocialLogins.Google,
        onlyLogin: false,
      });

      if (result) {
        authModel.api_token = result.token;
        saveAuth(authModel);
        const user = await getUserByToken(authModel.api_token);
        saveCurrentUser(user);
        navigate('/dashboard');
      } else if (token) {
        const signUpInfo = {
          token: token,
          provider: SocialLogins.Google
        }
        navigate('/auth/social-signup', { state: signUpInfo })
      }
      else {
        console.error('Social login failed');
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error('Error during Google sign-in:', error);
      setGoogleLoading(false);
    }
  };

  const fetchCountry = async () => {
    const ipData = await fetchIpData()
    signUpInfo.country = ipData.country
    setSignUpInfo(prevInfo => ({
      ...prevInfo,
      country: ipData.country
    }));
  }

  useEffect(() => {
    fetchCountry()
  }, [])

  useEffect(() => {
    const selectedCountry = CountryList.find(country => country.isoCode === signUpInfo.country);
    const currency = selectedCountry?.currencyCode || '';
    const timeZone = selectedCountry?.timeZone || '';

    setSignUpInfo(prevInfo => ({
      ...prevInfo,
      currency: currency,
      timeZone: timeZone
    }));
  }, [signUpInfo.country])

  useEffect(() => {
    const themeModeValue = localStorage.getItem('kt_theme_mode_value');

    const initializeGoogleSignUp = () => {
      if (!window.google || !window.google.accounts) return;
      window.google.accounts.id.initialize({
        client_id: '925718632458-3e5u51f0ne71jj4qk5ftpo3dk65csf2m.apps.googleusercontent.com',
        callback: handleGoogleSignUp,
      });
      const btn = document.getElementById('google-signup-btn');
      const containerWidth = btn?.offsetWidth || 250;
      if (btn) {
        window.google.accounts.id.renderButton(btn, {
          theme: (themeModeValue === 'dark') ? 'filled_black' : 'outline',
          size: 'large',
          text: 'signup_with',
          width: Math.min(containerWidth, 400),
        });
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignUp;
      document.body.appendChild(script);
    } else {
      initializeGoogleSignUp();
    }
  }, []);

  const renderContent = () => {
    switch (signUpStatus) {
      case SignupStatus.initialized:
        return <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_login_signup_form'
          onSubmit={formik.handleSubmit}
        >
          {/* begin::Heading */}
          <div className='text-center mb-11'>
            <h1 className='text-gray-900 fw-bolder mb-3'>{'Sign Up'.toTranslatedDisplay()}</h1>
          </div>

          {/* begin::Login options */}
          {/* begin::Login options */}
          <div className='row g-3 mb-9 myb-social-row'>
            {/* begin::Col - Google */}
            <div className='col-md-6' style={{ colorScheme: "light" }}>
              {googleLoading ? (
                <button className="btn btn-flex btn-outline btn-text-gray-700 bg-state-light flex-center text-nowrap w-100" disabled>
                  <span className='spinner-border spinner-border-sm me-2'></span>
                  {'Signing up with Google...'.toTranslatedDisplay()}
                </button>
              ) : (
                <div className='myb-google-btn'>
                  {/* Visible face matches the Apple button; Google's own (unstyleable) button sits invisibly on top to receive the click */}
                  <div className="btn btn-flex btn-outline btn-text-gray-700 bg-state-light flex-center text-nowrap w-100">
                    <svg width='16' height='16' viewBox='0 0 18 18' aria-hidden='true' className='me-3'><path fill='#4285F4' d='M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9a8.6 8.6 0 0 0 2.7-6.5z' /><path fill='#34A853' d='M9 18c2.4 0 4.5-.8 6-2.3l-2.9-2.2c-.8.6-1.9.9-3.1.9a5.4 5.4 0 0 1-5.1-3.7H.9v2.3A9 9 0 0 0 9 18z' /><path fill='#FBBC05' d='M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3z' /><path fill='#EA4335' d='M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3A5.4 5.4 0 0 1 9 3.6z' /></svg>
                    {'Sign up with Google'.toTranslatedDisplay()}
                  </div>
                  <div id="google-signup-btn" className='myb-google-hit'></div>
                </div>
              )}
            </div>
            {/* begin::Col - Apple */}
            <AppleSignInButton
              toAbsoluteUrl={toAbsoluteUrl}
              onAppleSignIn={handleAppleSignUp}
              className="btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100"
              isLoading={appleLoading}
              loadingText={'Signing up with Apple...'.toTranslatedDisplay()}
            />
          </div>

          <div className='separator separator-content my-10'>
            <span className='w-125px text-gray-500 fw-semibold fs-7'>{'Or with email'.toTranslatedDisplay()}</span>
          </div>

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          {/* Email Field */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>{'Email'.toTranslatedDisplay()}</label>
            <input
              placeholder={'Email'.toTranslatedDisplay()}
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                { 'is-valid': formik.touched.email && !formik.errors.email }
              )}
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.email?.toTranslatedDisplay()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className='fv-row mb-8' data-kt-password-meter='true'>
            <div className='mb-1'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>{'Password'.toTranslatedDisplay()}</label>
              <div className='position-relative mb-3'>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={'Password'.toTranslatedDisplay()}
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      { 'is-invalid': formik.touched.password && formik.errors.password },
                      { 'is-valid': formik.touched.password && !formik.errors.password }
                    )}
                  />
                  <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                    {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                  </span>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password?.toTranslatedDisplay()}</span>
                    </div>
                  </div>
                )}
              </div>
              {/* Password Strength Meter */}
              <div className='d-flex align-items-center mb-3' data-kt-password-meter-control='highlight'>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
              </div>
            </div>
            <div className='text-muted'>{'Use 8 or more characters with a mix of letters, numbers & symbols.'.toTranslatedDisplay()}</div>
          </div>

          {/* Confirm Password Field */}
          <div className='fv-row mb-5'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>{'Confirm Password'.toTranslatedDisplay()}</label>
            <div className="input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={'Password confirmation'.toTranslatedDisplay()}
                autoComplete='off'
                {...formik.getFieldProps('changepassword')}
                className={clsx(
                  'form-control bg-transparent',
                  { 'is-invalid': formik.touched.changepassword && formik.errors.changepassword },
                  { 'is-valid': formik.touched.changepassword && !formik.errors.changepassword }
                )}
              />
              <span className="input-group-text" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
              </span>
            </div>
            {formik.touched.changepassword && formik.errors.changepassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.changepassword?.toTranslatedDisplay()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className='fv-row mb-8'>
            <label className='form-check form-check-inline' htmlFor='kt_login_toc_agree'>
              <input
                className='form-check-input'
                type='checkbox'
                id='kt_login_toc_agree'
                {...formik.getFieldProps('acceptTerms')}
              />
              <span>
                {'I Accept the'.toTranslatedDisplay()} {' '}
                <a href='https://zetran.com/termsofservice' target='_blank' className='ms-1 link-primary'>
                  {'Terms'.toTranslatedDisplay()}
                </a>
                .
              </span>
            </label>
            {formik.touched.acceptTerms && formik.errors.acceptTerms && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.acceptTerms?.toTranslatedDisplay()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting}
            >
              {!loading && <span className='indicator-label'>{'Get Started'.toTranslatedDisplay()}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {'Please wait...'.toTranslatedDisplay()} {' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <Link to='/auth/login'>
              <button
                type='button'
                id='kt_login_signup_form_cancel_button'
                className='btn btn-lg btn-light-primary w-100 mb-5'
              >{'Cancel'.toTranslatedDisplay()}</button>
            </Link>
            {brandSettings.showForMainBrand && (
              <Link to='/demo' id='kt_signup_demo_link' className='btn btn-lg btn-outline btn-outline-dashed btn-outline-primary w-100'>
                {'Try the demo instead'.toTranslatedDisplay()}
              </Link>
            )}
          </div>
        </form>

      case SignupStatus.emailVerified:
        return <>
          <div className='text-center mb-11'>
            <h1 className='text-gray-900 fw-bolder mb-3'>{'One Time Code!'.toTranslatedDisplay()}</h1>
          </div>
          {otpError.length > 0 && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{otpError}</div>
            </div>
          )}
          <div className='d-flex justify-content-center '>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              inputStyle='inputStyle'
              renderSeparator={<span>-</span>}
              renderInput={(props) => <input {...props} />}
            />
          </div>
          <div className='text-muted p-5 mt-5 text-align-center'>{"Please enter the one-time code sent to your email. Don't forget to check your spam folder!".toTranslatedDisplay()}</div>
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              onClick={handleOTPValidation}
            >
              {!loading && <span className='indicator-label'>{'Check OTP'.toTranslatedDisplay()}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {'Please wait...'.toTranslatedDisplay()} {' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </>

      case SignupStatus.otpVerified:
        return <>
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>{'Business Name'.toTranslatedDisplay()}</label>
            <input
              placeholder={'Your business name'.toTranslatedDisplay()}
              name='bizName'
              onChange={handleSignupFormInput}
              type='text'
              autoComplete='off'
              className={clsx('form-control bg-transparent')}
            />
          </div>

          <div className='fv-row mb-8'>
            <EasyPicker
              name='bizType'
              value={signUpInfo.bizType}
              label={'Business Type'.toTranslatedDisplay()}
              options={businessTypes}
              onSelectionChange={handleSignupFormInput}
              placeholder={'Select business type'.toTranslatedDisplay()}
            />
          </div>

          <div className='fv-row mb-8'>
            <CountryPicker
              label={'Country'.toTranslatedDisplay()}
              value={signUpInfo?.country}
              onSelectionChange={(countryId: string) => {
                setSignUpInfo(prevInfo => ({
                  ...prevInfo,
                  country: countryId
                }));
              }}
            />
          </div>

          <div className='fv-row mb-8'>
            <CurrencyPicker
              label={'Business Currency'.toTranslatedDisplay()}
              selectedCurrency={signUpInfo?.currency}
              onSelectionChange={(currencyId: string) => {
                setSignUpInfo(prevInfo => ({
                  ...prevInfo,
                  currency: currencyId
                }));
              }}
            />
          </div>

          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              onClick={handleSignup}
            >
              {!loading && <span className='indicator-label'>{'Signup'.toTranslatedDisplay()}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {'Please wait...'.toTranslatedDisplay()} {' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </>
    }
  }

  return (
    <div>
      {renderContent()}
    </div>
  );
}

// Keep the existing SocialSignUp component unchanged
export function SocialSignUp() {
  const location = useLocation();
  const signUpInfo: any = location.state;
  const { saveAuth, saveCurrentUser } = useAuth();
  const [socialSignUpDto, setSocialSignUpDto] = useState<SocialSignUpDto>();
  const [loading, setLoading] = useState(false);

  // Add this function inside the SocialSignUp component, before the existing handleSignupFormInput
  const fetchCountry = async () => {
    const ipData = await fetchIpData()
    if (!ipData) return
    setSocialSignUpDto(prevInfo => ({
      ...prevInfo,
      country: ipData.country
    }));
  }

  // Add this useEffect to automatically fetch country on component mount
  useEffect(() => {
    fetchCountry()
  }, [])

  // Add this useEffect to automatically set currency based on country
  useEffect(() => {
    if (socialSignUpDto?.country) {
      const selectedCountry = CountryList.find(country => country.isoCode === socialSignUpDto.country);
      const currency = selectedCountry?.currencyCode || '';
      const timeZone = selectedCountry?.timeZone || '';

      setSocialSignUpDto(prevInfo => ({
        ...prevInfo,
        currency: currency,
        timeZone: timeZone
      }));
    }
  }, [socialSignUpDto?.country])

  const handleSignupFormInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null
  ) => {
    if (e) {
      const { name, value } = e.target;
      setSocialSignUpDto((prevInfo) => ({
        ...prevInfo,
        [name]: value,
      }));
    }
  };

  const handleDropdownChange = (name: string, value: string | undefined) => {
    setSocialSignUpDto((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  useEffect(() => {
    setSocialSignUpDto((prevInfo) => ({
      ...prevInfo,
      sToken: signUpInfo?.token || "",
      provider: signUpInfo?.provider || "",
    }));
  }, [signUpInfo]);

  const handleSignup = async () => {
    try {
      if (socialSignUpDto) {
        setLoading(true)
        socialSignUpDto.brand = brand
        socialSignUpDto.language = getCurrentLang()
        const signupResponse = await socialSignup(socialSignUpDto);
        if (signupResponse) {
          // Use the appropriate provider for the social login
          const result = await socialLogin({
            sToken: socialSignUpDto.sToken || "",
            provider: socialSignUpDto.provider || SocialLogins.Google, // Use the actual provider
            onlyLogin: false,
          });
          if (result) {
            authModel.api_token = result.token;
            saveAuth(authModel);
            const user = await getUserByToken(authModel.api_token);
            saveCurrentUser(user);
            // MBAI-500/502/517: signup completed (social flow)
            trackEvent(AnalyticsEvent.SignUp, { method: socialSignUpDto.provider || 'social' })
          }
        }
        setLoading(false)
      }
    } catch (error: any) {
      setLoading(false)
    }
  };

  return (
    <>
      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Business Name'.toTranslatedDisplay()}</label>
        <input
          placeholder={'Your business name'.toTranslatedDisplay()}
          name="bizName"
          onChange={handleSignupFormInput}
          type="text"
          autoComplete="off"
          className="form-control bg-transparent"
        />
      </div>

      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Business Type'.toTranslatedDisplay()}</label>
        <EasyPicker
          name='bizType'
          value={socialSignUpDto?.bizType}
          options={businessTypes}
          onSelectionChange={handleSignupFormInput}
        />
      </div>

      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Country'.toTranslatedDisplay()}</label>
        <CountryPicker
          value={socialSignUpDto?.country}
          onSelectionChange={(value) => handleDropdownChange("country", value)}
        />
      </div>

      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Currency'.toTranslatedDisplay()}</label>
        <CurrencyPicker
          selectedCurrency={socialSignUpDto?.currency}
          onSelectionChange={(value) => handleDropdownChange("currency", value)}
        />
      </div>

      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_up_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          onClick={handleSignup}
        >
          {!loading && <span className="indicator-label">{'Signup'.toTranslatedDisplay()}</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              {'Please wait...'.toTranslatedDisplay()} {" "}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
    </>
  );
}